import type { FetchShortsOptions } from '~/composables/shorts/useFetchShorts';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { useFetchShorts } from '~/composables/shorts/useFetchShorts';
import { useShortsFeedStore } from '~/store/shortsFeedStore';

export function useFeedState() {
	const store = useShortsFeedStore();
	const { fetchVideos } = useFetchShorts();

	async function fetchFeed(
		filters?: ShortsFilters,
		options: FetchShortsOptions = {}
	): Promise<void> {
		const capturedId = store.guardNext();
		store.isLoading = true;
		store.activeFilters = filters ?? {};

		try {
			const result = await fetchVideos(filters ?? {}, 1, undefined, options);
			if (store.guardIsStale(capturedId)) return;
			store.setVideos(result.items);
			store.activeFilters = result.appliedFilters;
			store.hasMore = result.hasMore;
			store.allWatched = result.allWatched ?? false;
			store.totalAvailable = result.totalAvailable ?? 0;
			store.currentPage = 1;
			store.isPreloaded = true;
		} finally {
			if (!store.guardIsStale(capturedId)) {
				store.isLoading = false;
			}
		}
	}

	async function loadMore(): Promise<void> {
		if (store.isLoading || !store.hasMore) return;

		const capturedId = store.guardNext();
		store.isLoading = true;

		try {
			const nextPage = store.currentPage + 1;
			const result = await fetchVideos(store.activeFilters, nextPage);
			if (store.guardIsStale(capturedId)) return;
			store.appendVideos(result.items);
			store.activeFilters = result.appliedFilters;
			store.hasMore = result.hasMore;
			store.allWatched = result.allWatched ?? store.allWatched;
			store.totalAvailable = result.totalAvailable ?? store.totalAvailable;
			store.currentPage = nextPage;
		} finally {
			if (!store.guardIsStale(capturedId)) {
				store.isLoading = false;
			}
		}
	}

	async function applyFilters(
		filters: ShortsFilters,
		options: FetchShortsOptions = {}
	): Promise<void> {
		store.guardNext();
		store.reset();
		store.activeFilters = filters;
		store.isLoading = true;

		await fetchFeed(filters, options);
	}

	function reset(): void {
		store.reset();
	}

	return { fetchFeed, loadMore, applyFilters, reset };
}
